import React, {
  FunctionComponent,
  createElement,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { UseQueryResult } from 'react-query'
import { PaginationState } from '@tanstack/react-table'
import { LoadingComponent, Pagination } from '@demandlocal/components'
import { AnimatePresence, motion } from 'framer-motion'

interface Props {
  fetchFn: (args: any) => UseQueryResult<any, unknown>
  gridComponent: FunctionComponent<any>
  filters: Record<string, unknown>
  sorting?: Record<string, string | undefined>
  setTotalRows?: (v: number) => void
  gridItemProps?: Record<string, unknown>
  emptyLabel?: string
  indexKey?: string
}

const Grid = ({
  gridComponent,
  fetchFn,
  filters,
  sorting,
  setTotalRows,
  gridItemProps,
  emptyLabel = 'No data',
  indexKey,
}: Props): JSX.Element => {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 20,
  })
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  )

  const remapSortingToArray = (
    s: Record<string, string | undefined> | undefined,
  ): Array<Record<string, string | boolean>> => {
    if (s) {
      return Object.entries(s)
        .filter(([, val]) => val !== undefined && val !== '')
        .map(([key, val]) => ({ id: key, [val as string]: true }))
    }
    return []
  }

  const fetchDataOptions = {
    pageIndex,
    pageSize,
    filters,
    sorting: remapSortingToArray(sorting),
  }

  const { data, isPreviousData, isLoading } = fetchFn(fetchDataOptions)
  const hasData = data?.data?.length > 0

  useEffect(() => {
    if (setTotalRows) setTotalRows(data?.pagination?.total ?? 0)
  }, [setTotalRows, data?.pagination?.total])

  return (
    <div
      className={`flex flex-1 relative flex-col gap-4 ${
        isLoading ? 'min-h-[256px]' : ''
      }`}>
      {isLoading && <LoadingComponent title={'Loading ...'} />}
      <div className={'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5'}>
        <AnimatePresence>
          {data?.data?.map((item: any, index: number) => {
            return isPreviousData ? (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={'bg-cultured-gray h-auto'}>
                <div className={'invisible'}>
                  {createElement(gridComponent, { item })}
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={index}>
                {createElement(gridComponent, { item, ...gridItemProps })}
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
      {!hasData && !isLoading && (
        <div className={' w-full h-24 flex flex-col justify-center items-center'}>
          {emptyLabel}
        </div>
      )}
      {pagination.pageSize + 1 > pagination.pageSize && !isLoading && hasData && (
        <Pagination
          nextPage={() =>
            setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }))
          }
          previousPage={() =>
            setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }))
          }
          gotoPage={(page) => {
            if (page > 0 && page <= data?.pagination?.lastPage)
              setPagination((prev) => ({ ...prev, pageIndex: page }))
          }}
          pageCount={data?.pagination?.lastPage}
          page={pagination.pageIndex}
        />
      )}
    </div>
  )
}

export default Grid
